import React from "react";
import SEO from "@/components/SEO";
import { useContent } from "@/context/ContentContext";
import { Mic } from "lucide-react";

function ytId(url) {
  const m = (url || "").match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
}

export default function Podcast() {
  const { c } = useContent();
  const sidebar = c("podcast_sidebar");

  // Episodes: prefer structured JSON (url + description), fall back to legacy
  // one-link-per-line `podcast_videos`.
  let episodes = [];
  try {
    const raw = c("podcast_episodes");
    if (raw) episodes = JSON.parse(raw);
  } catch (e) { episodes = []; }
  if (!Array.isArray(episodes) || episodes.length === 0) {
    episodes = (c("podcast_videos") || "").split(/[\n,]+/).map((s) => s.trim()).filter(Boolean)
      .map((u) => ({ url: u, description: "" }));
  }
  episodes = episodes.map((e) => ({ ...e, id: ytId(e.url) })).filter((e) => e.id);

  return (
    <>
      <SEO title={c("podcast_title", "Podcast")} path="/podcast"
        description="De VoiceOverKids-podcast — gesprekken over stemacteren door kinderen, casting en de branche." />
      <section className="bg-brand-ink text-white">
        <div className="container-x py-14 md:py-16">
          <span className="label-eyebrow inline-flex items-center gap-2 text-brand-purple"><Mic size={14} /> Podcast</span>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter md:text-5xl">{c("podcast_title", "De VoiceOverKids Podcast")}</h1>
          {c("podcast_intro") && <p className="mt-4 max-w-2xl whitespace-pre-line text-zinc-300">{c("podcast_intro")}</p>}
        </div>
      </section>

      <section className="container-x section-y">
        <div className={`grid gap-10 ${sidebar ? "lg:grid-cols-3" : ""}`}>
          <div className={sidebar ? "lg:col-span-2" : ""}>
            {episodes.length === 0 ? (
              <div className="rounded-3xl border border-zinc-200 bg-white p-10 text-center text-zinc-600">
                Afleveringen binnenkort beschikbaar.
              </div>
            ) : (
              <div className="space-y-6">
                {episodes.map((ep, i) => (
                  <div key={`${ep.id}-${i}`} data-testid={`podcast-episode-${i}`}
                    className="grid items-start gap-5 rounded-2xl border border-zinc-200 bg-white p-4 md:grid-cols-2">
                    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-black">
                      <div className="aspect-video w-full">
                        <iframe title={ep.id} src={`https://www.youtube.com/embed/${ep.id}?rel=0`} className="h-full w-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                      </div>
                    </div>
                    <div className="min-w-0">
                      {ep.description
                        ? <p className="whitespace-pre-line leading-relaxed text-zinc-600">{ep.description}</p>
                        : <p className="text-sm text-zinc-400">Nog geen beschrijving voor deze aflevering.</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {sidebar && (
            <aside className="lg:col-span-1" data-testid="podcast-sidebar">
              <div className="sticky top-24 rounded-3xl border border-zinc-200 bg-brand-paper p-6">
                <h2 className="font-heading text-xl font-bold text-zinc-900">{c("podcast_sidebar_title", "Over de show")}</h2>
                <p className="mt-3 whitespace-pre-line leading-relaxed text-zinc-600">{sidebar}</p>
              </div>
            </aside>
          )}
        </div>
      </section>
    </>
  );
}
